import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ui } from "../config/theme";

import { 
    Briefcase, MapPin, DollarSign, Calendar, 
    FileText, Users, ArrowLeft, Loader2, Trash2 
} from 'lucide-react';
import { API_BASE_URL } from "../config/apiconfig";

const EditJobPost = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [countries, setCountries] = useState([]);
    const [lkLocations, setLkLocations] = useState([]);
    const isSriLanka = useMemo(() => (job?.location_country || '') === 'Sri Lanka', [job?.location_country]);
    const provinceOptions = useMemo(() => (lkLocations || []).map(p => p.province), [lkLocations]);
    const districtOptions = useMemo(() => {
        const p = (lkLocations || []).find(x => x.province === job?.province);
        return p ? (p.districts || []).map(d => d.district) : [];
    }, [lkLocations, job?.province]);
    
    useEffect(() => {
        const fetchJob = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/job-postings/${id}`);
                if (!response.ok) throw new Error('Failed to fetch job');
                const data = await response.json();
                
                // Verify the job belongs to the current user
                if (user?.role === 'recruiter' && data.posted_by !== user.id) {
                    navigate('/recruiter-dashboard');
                    return;
                }
                
                setJob(data);
            } catch (err) {
                console.error(err);
                navigate('/recruiter-dashboard');
            } finally {
                setLoading(false);
            }
        };
        fetchJob();
    }, [id, user, navigate]);

    // Load countries and LK locations
    useEffect(() => {
        (async () => {
            try {
                const r = await fetch('https://general.apivcm.shop/countries', { cache: 'no-store' });
                if (r.ok) {
                    const j = await r.json();
                    const arr = Array.isArray(j?.data) ? j.data : [];
                    const normalized = arr.map(c => ({
                        id: c.id,
                        label: c.country,
                        value: c.country_code === 'LK' ? 'Sri Lanka' : c.country,
                        code: c.country_code
                    }));
                    const hasLK = normalized.some(c => c.value === 'Sri Lanka');
                    setCountries(hasLK ? normalized : [{ id: 'LK', label: 'Sri Lanka', value: 'Sri Lanka', code: 'LK' }, ...normalized]);
                }
            } catch {}
        })();
        (async () => {
            try {
                const r = await fetch('https://general.apivcm.shop/sri-lanka-locations', { cache: 'no-store' });
                if (r.ok) {
                    const j = await r.json();
                    setLkLocations(Array.isArray(j?.data) ? j.data : []);
                }
            } catch {}
        })();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Validate region fields similar to create form
            if (!job.location_country) {
                alert('Country is required');
                setSaving(false);
                return;
            }
            if (job.location_country === 'Sri Lanka') {
                if (!job.province || !job.district) {
                    alert('Province and District are required for Sri Lanka');
                    setSaving(false);
                    return;
                }
            }

            const composedLocation = (job.location && String(job.location).trim().length > 0)
                ? job.location
                : (job.location_country === 'Sri Lanka'
                    ? [job.district, job.province, 'Sri Lanka'].filter(Boolean).join(', ')
                    : job.location_country);

            const response = await fetch(`${API_BASE_URL}/api/job-postings/${id}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    ...job,
                    location: composedLocation,
                    posted_by: user.id // Ensure the job stays with the same recruiter
                })
            });
            
            if (!response.ok) throw new Error('Update failed');
            navigate('/recruiter-dashboard');
        } catch (err) {
            alert('Error updating job: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    // Add this function inside your EditJobPost component
    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this job posting? This action cannot be undone.")) {
            return;
        }

        setDeleting(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/job-postings/${id}`, {
                method: 'DELETE',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    posted_by: user.id // Send the recruiter ID for verification
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to delete job');
            }

            navigate('/recruiter-dashboard');
        } catch (err) {
            console.error("Delete job error:", {
                error: err.message,
                jobId: id,
                recruiterId: user?.id,
                timestamp: new Date().toISOString()
            });
            alert(`Error deleting job: ${err.message}`);
        } finally {
            setDeleting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setJob({
            ...job, 
            [name]: type === 'checkbox' ? checked : value
        });
    };

    if (loading) return (
        <div className="flex justify-center items-center h-screen">
            <Loader2 className="animate-spin h-12 w-12 text-blue-500" />
        </div>
    );
    
    if (!job) return (
        <div className="text-center py-20">
            <h2 className="text-xl font-semibold">Job not found</h2>
            <button 
                onClick={() => navigate('/recruiter-dashboard')}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
                Back to Dashboard
            </button>
        </div>
    );

    return (
        <div className={ui.page}>
            <Header />
            <main className="flex-grow">
                <div className={ui.container}>
                    <div className="w-full mb-6">
                        <button 
                            onClick={() => navigate(-1)} 
                            className={ui.buttonLink}
                        >
                            <ArrowLeft className="w-5 h-5 mr-2" /> Back
                        </button>
                    </div>
                
                    <div className={`${ui.sectionCard} p-8`}>
                        <div className="flex justify-between items-center mb-8">
                            <h1 className="text-3xl font-semibold text-gray-900">Edit Job Posting</h1>
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                            >
                                {deleting ? (
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                ) : (
                                    <Trash2 className="w-5 h-5 mr-2" />
                                )}
                                Delete
                            </button>
                        </div>
                    
                        <form onSubmit={handleSubmit} className="space-y-8">
                            {/* Job Information Section */}
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-6">Job Information</h2>
                                
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                                            Job Title <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="title"
                                            name="title"
                                            value={job.title || ''}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            placeholder="e.g. Software Engineer"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                                                Department <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                id="department"
                                                name="department"
                                                value={job.department || ''}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            >
                                                <option value="">Select...</option>
                                                <option value="Engineering">Engineering</option>
                                                <option value="Marketing">Marketing</option>
                                                <option value="Sales">Sales</option>
                                                <option value="HR">Human Resources</option>
                                                <option value="Finance">Finance</option>
                                                <option value="Operations">Operations</option>
                                                <option value="Design">Design</option>
                                                <option value="Product">Product</option>
                                            </select>
                                        </div>

                                        <div className="space-y-2">
                                            <label htmlFor="employment_type" className="block text-sm font-medium text-gray-700">
                                                Employment Type <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                id="employment_type"
                                                name="employment_type"
                                                value={job.employment_type || ''}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            >
                                                <option value="">Select...</option>
                                                <option value="Full-time">Full-time</option>
                                                <option value="Part-time">Part-time</option>
                                                <option value="Contract">Contract</option>
                                                <option value="Temporary">Temporary</option>
                                                <option value="Internship">Internship</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="classification" className="block text-sm font-medium text-gray-700">
                                            Classification <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            id="classification"
                                            name="classification"
                                            value={job.classification || ''}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        >
                                            <option value="">Select...</option>
                                            <option value="Entry Level">Entry Level</option>
                                            <option value="Mid Level">Mid Level</option>
                                            <option value="Senior Level">Senior Level</option>
                                            <option value="Executive">Executive</option>
                                            <option value="Manager">Manager</option>
                                            <option value="Director">Director</option>
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="skills" className="block text-sm font-medium text-gray-700">
                                            Skills
                                        </label>
                                        <input
                                            type="text"
                                            id="skills"
                                            name="skills"
                                            value={job.skills || ''}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            placeholder="Enter skills separated by commas"
                                        />
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="is_remote"
                                            name="is_remote"
                                            checked={job.is_remote || false}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="is_remote" className="ml-2 text-sm text-gray-700">
                                            This is a work from home/remote position
                                        </label>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Country <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            name="location_country"
                                            value={job.location_country || 'Sri Lanka'}
                                            onChange={(e) => setJob(prev => ({ ...prev, location_country: e.target.value || 'Sri Lanka', province: e.target.value === 'Sri Lanka' ? (prev.province || '') : '', district: e.target.value === 'Sri Lanka' ? (prev.district || '') : '' }))}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        >
                                            {countries.map(c => (
                                                <option key={c.id} value={c.value}>{c.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {isSriLanka && (
                                        <>
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">Province <span className="text-red-500">*</span></label>
                                            <select
                                                name="province"
                                                value={job.province || ''}
                                                onChange={(e) => setJob(prev => ({ ...prev, province: e.target.value, district: '' }))}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            >
                                                <option value="">Select...</option>
                                                {provinceOptions.map(p => (<option key={p} value={p}>{p}</option>))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">District <span className="text-red-500">*</span></label>
                                            <select
                                                name="district"
                                                value={job.district || ''}
                                                onChange={(e) => setJob(prev => ({ ...prev, district: e.target.value }))}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                                disabled={!job.province}
                                            >
                                                <option value="">Select...</option>
                                                {districtOptions.map(d => (<option key={d} value={d}>{d}</option>))}
                                            </select>
                                        </div>
                                        </>
                                    )}
                                    {!isSriLanka && (
                                        <div className="space-y-2">
                                            <label htmlFor="location" className="block text-sm font-medium text-gray-700">Location</label>
                                            <input
                                                type="text"
                                                id="location"
                                                name="location"
                                                value={job.location || ''}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                                placeholder="City or region"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Job Description Section */}
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-6">Job Description</h2>
                                
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                            Job Description <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            id="description"
                                            name="description"
                                            rows="6"
                                            value={job.description || ''}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                                            placeholder="Describe the role, responsibilities, and requirements..."
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="responsibilities" className="block text-sm font-medium text-gray-700">
                                            Key Responsibilities <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            id="responsibilities"
                                            name="responsibilities"
                                            rows="4"
                                            value={job.responsibilities || ''}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                                            placeholder="List the main responsibilities and duties..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Salary Information Section */}
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-6">Salary Information</h2>
                                
                                <div className="space-y-6">
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="include_salary"
                                            name="include_salary"
                                            checked={job.include_salary || false}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="include_salary" className="ml-2 text-sm text-gray-700">
                                            Include Salary Information
                                        </label>
                                    </div>

                                    {job.include_salary && (
                                        <div className="space-y-6 p-4 bg-gray-50 rounded-lg">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <label htmlFor="currency" className="block text-sm font-medium text-gray-700">
                                                        Currency <span className="text-red-500">*</span>
                                                    </label>
                                                    <select
                                                        id="currency"
                                                        name="currency"
                                                        value={job.currency || 'USD'}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                                    >
                                                        <option value="USD">USD</option>
                                                        <option value="EUR">EUR</option>
                                                        <option value="GBP">GBP</option>
                                                        <option value="CAD">CAD</option>
                                                        <option value="AUD">AUD</option>
                                                    </select>
                                                </div>

                                                <div className="space-y-2">
                                                    <label htmlFor="paid_every" className="block text-sm font-medium text-gray-700">
                                                        Paid Every <span className="text-red-500">*</span>
                                                    </label>
                                                    <select
                                                        id="paid_every"
                                                        name="paid_every"
                                                        value={job.paid_every || 'Month'}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                                    >
                                                        <option value="Month">Month</option>
                                                        <option value="Year">Year</option>
                                                        <option value="Hour">Hour</option>
                                                        <option value="Week">Week</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <label htmlFor="minimum_salary" className="block text-sm font-medium text-gray-700">
                                                        Minimum Salary <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        id="minimum_salary"
                                                        name="minimum_salary"
                                                        value={job.minimum_salary || ''}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                                        placeholder="0"
                                                    />
                                                </div>

                                                <div className="space-y-2">
                                                    <label htmlFor="maximum_salary" className="block text-sm font-medium text-gray-700">
                                                        Maximum Salary <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        id="maximum_salary"
                                                        name="maximum_salary"
                                                        value={job.maximum_salary || ''}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                                        placeholder="0"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Legacy salary field for backward compatibility */}
                                    {!job.include_salary && (
                                        <div className="space-y-2">
                                            <label htmlFor="salary" className="block text-sm font-medium text-gray-700">
                                                Salary (Optional)
                                            </label>
                                            <input
                                                type="number"
                                                id="salary"
                                                name="salary"
                                                value={job.salary || ''}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                                placeholder="e.g. 120000"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Application Details */}
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-6">Application Details</h2>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label htmlFor="closing_date" className="block text-sm font-medium text-gray-700">
                                            Application Closing Date <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            id="closing_date"
                                            name="closing_date"
                                            value={job.closing_date ? job.closing_date.split('T')[0] : ''}
                                            onChange={handleChange}
                                            required
                                            min={new Date().toISOString().split('T')[0]}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                                            Status
                                        </label>
                                        <select
                                            id="status"
                                            name="status"
                                            value={job.status || 'Active'}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Closed">Closed</option>
                                            <option value="Draft">Draft</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => navigate('/recruiter-dashboard')}
                                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-500 focus:ring-opacity-50 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        "Save Changes"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default EditJobPost;